import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setModalState } from "../../../store/modal/ModalActions";
import {
  loadCallerAccounts,
  loadAllAccountDetails,
  setTokenEntitlements,
} from "../../../store/app-config/AppConfigActions";
import {
  selectCallerAccountId,
  selectCallerAccounts,
  selectIsCallerAccountsInvoked,
  selectIsFetchingCallerAccountsFailed,
} from "../../../store/app-config/AppConfigSelectors";
import { CircularProgress } from "@material-ui/core";
import { setCallerAccount } from "../../../utils/SmartMerchandiserAPI";
import { useQuery } from "../../../hooks/useQueryParams";
import { useStyles } from "./CallerAccountModalStyled";
import CallerAccountsErrorModal from "./CallerAccountsErrorModal";
import { userLoggedOut } from "../../../services/Auth0UserService";
import { getEntitlements } from "../../../services/UserService";
import { useHasAllEntitlements } from "hooks/useEntitlement";
import { asyncEventsWebSocketService } from "../../../services/AsyncEventsWebSocketService";
import {
  fetchSearchReindexRequests,
  searchReindexCompleted,
  searchReindexStarted,
} from "../../../store/search-reindex/SearchReindexActions";
import {
  websocketCategorySaveStarted,
  websocketCategorySaveCompleted,
  websocketCategorySaveFailed,
  fetchInProgressRequestsSnapshot,
} from "../../../store/product-list/ProductListActions";

const AccountSelection = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const callerAccounts = useSelector(selectCallerAccounts);
  const isCallerAccountsInvoked = useSelector(selectIsCallerAccountsInvoked);
  const isFetchingFailed = useSelector(selectIsFetchingCallerAccountsFailed);
  const query = useQuery();
  const userAccountIdQuery = query.get("accountId") ?? "";
  const selectedCallerAccountId = useSelector(selectCallerAccountId);
  const isUserLoggedOut = userLoggedOut();
  const hasCatalogEntitlement = useHasAllEntitlements(["catalog"]);

  useEffect(() => {
    if (selectedCallerAccountId === "" && !isUserLoggedOut) {
      dispatch(loadCallerAccounts());
    }
  }, [dispatch, selectedCallerAccountId, isUserLoggedOut]);

  useEffect(() => {
    if (callerAccounts.accounts?.length > 0 && isCallerAccountsInvoked) {
      getEntitlements().then((tokenEntitlements) => {
        if (tokenEntitlements && Object.keys(tokenEntitlements).length > 0) {
          Object.entries(tokenEntitlements).forEach(
            ([accountId, entitlements]) => {
              dispatch(setTokenEntitlements(accountId, entitlements));
            },
          );
        }
      });
    }
  }, [dispatch, isCallerAccountsInvoked]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (callerAccounts.accounts?.length === 0 && isCallerAccountsInvoked) {
      dispatch(setModalState("ErrorAccountModalId", true));
    } else if (
      callerAccounts.accounts?.length === 1 &&
      selectedCallerAccountId === ""
    ) {
      const accountId = callerAccounts.accounts[0].accountId;
      if (userAccountIdQuery === "") {
        setCallerAccount(accountId);
      }
      dispatch(loadAllAccountDetails({ accountId }));
    } else if (
      callerAccounts.accounts?.length > 1 &&
      selectedCallerAccountId === ""
    ) {
      if (userAccountIdQuery === "") {
        dispatch(setModalState("AccountModalId", true));
      } else if (userAccountIdQuery !== "") {
        dispatch(loadAllAccountDetails({ accountId: userAccountIdQuery }));
      }
    }
  }, [
    dispatch,
    callerAccounts,
    isCallerAccountsInvoked,
    userAccountIdQuery,
    selectedCallerAccountId,
    hasCatalogEntitlement,
  ]);

  useEffect(() => {
    if (selectedCallerAccountId === "" && userAccountIdQuery !== "") {
      setCallerAccount(userAccountIdQuery);
    }
  }, [selectedCallerAccountId, userAccountIdQuery]);

  // Initialize WebSocket when accountId is available
  useEffect(() => {
    if (selectedCallerAccountId && selectedCallerAccountId !== "") {
      // Set message handler - component handles dispatching (keeps service pure)
      asyncEventsWebSocketService.setMessageHandler((message) => {
        switch (message.type) {
          case "category.save.started":
            dispatch(websocketCategorySaveStarted(message.categoryId));
            break;
          case "category.save.completed": {
            const { categoryId, errorMessage, requestId, statusCode } = message;
            // Backend sends category.save.completed for both success and failure
            // Check errorMessage to determine if it failed
            if (errorMessage) {
              console.error("❌ Category save failed:", {
                categoryId,
                requestId,
                statusCode,
                errorMessage,
              });
              if (requestId) {
                dispatch(websocketCategorySaveFailed(categoryId, requestId, errorMessage));
              }
            } else {
              if (requestId) {
                dispatch(websocketCategorySaveCompleted(categoryId, requestId));
              }
            }
            break;
          }
          case "search.reindex.started":
            dispatch(
              searchReindexStarted({
                requestId: message.requestId,
                storeId: message.storeId,
                startedAt: message.startedAt,
              }),
            );
            break;
          case "search.reindex.completed":
            dispatch(
              searchReindexCompleted({
                requestId: message.requestId,
                storeId: message.storeId,
                statusCode: message.statusCode,
                status: message.status,
                executionId: message.executionId,
                reason: message.reason,
                errorMessage: message.errorMessage,
              }),
            );
            break;
          default:
            console.log("⚠️ Unknown WebSocket message type:", message);
        }
      });

      // Set onOpen handler - fetch snapshot after WebSocket connects (initial connection)
      asyncEventsWebSocketService.setOnOpenHandler(() => {
        // Dispatch action to fetch in-progress requests snapshot
        dispatch(fetchInProgressRequestsSnapshot(selectedCallerAccountId));
      });

      // Set onReconnect handler - fetch snapshot after reconnection to catch missed messages
      asyncEventsWebSocketService.setOnReconnectHandler(() => {
        // Dispatch action to fetch snapshot which will check for completed saves we missed
        dispatch(fetchInProgressRequestsSnapshot(selectedCallerAccountId));
        dispatch(fetchSearchReindexRequests());
      });

      // Connect WebSocket with accountId
      asyncEventsWebSocketService.connect(selectedCallerAccountId);

      // Cleanup on unmount or when accountId changes
      return () => {
        asyncEventsWebSocketService.disconnect();
      };
    }
  }, [dispatch, selectedCallerAccountId]);

  return (
    <>
      {callerAccounts.accounts?.length === 0 &&
      !isCallerAccountsInvoked &&
      !isFetchingFailed ? (
        <div className={classes.overlay}>
          <CircularProgress />
        </div>
      ) : (
        callerAccounts.accounts?.length === 0 &&
        isCallerAccountsInvoked && <CallerAccountsErrorModal />
      )}
    </>
  );
};

export default AccountSelection;
